import cookie from 'cookie';
import queryString from 'query-string';
import mapKeyValues, { transforms as t } from 'lib/mapKeyValues';
import normalizePageType from 'lib/normalizePageType';

/**
 * Standardizes the site value to the base site value (example: hgtv-photos -> hgtv)
 * @param	{string} siteValue Unmodified site value
 * @return {string} Modified site value
 */
export function normalizeSiteValue(siteValue) {
	const siteValueMap = [
		{ test: /^(golden-)?hgtv([-_]\w+)?$/i, value: 'hgtv' },
		{ test: /^(golden-)?food([-_]\w+)?$/i, value: 'food' },
		{ test: /^(golden-)?tlc([-_]\w+)?$/i, value: 'tlc' },
		{ test: /^(golden-)?discovery([-_]\w+)?$/i, value: 'dsc/discovery' },
		{ test: /^(golden-)?investigationdiscovery([-_]\w+)?$/i, value: 'investigationdiscovery' },
	];

	const match = siteValueMap.find(item => item.test instanceof RegExp && item.test.exec(siteValue));
	return match ? match.value : siteValue;
}

const isSweepsPage = (editorialtracking) => 
	editorialtracking === 'sweeps_entry' || editorialtracking === 'sweeps_thanks';

const isSweepsEntry = (editorialtracking) => editorialtracking === 'sweeps_entry';

const isSweepsThanks = (editorialtracking) => editorialtracking === 'sweeps_thanks';

const overridePageType = (pagetype, otherMeta) => {
	if (otherMeta.editorialtracking === 'sweeps_entry') return 'sweepstakes_entry';
	if (otherMeta.editorialtracking === 'sweeps_thanks') return 'sweepstakes_thanks';
	return pagetype;
};

const parseTagGroup = (tagGroup) => {
	if (!tagGroup) return {};
	const tags = {};
	const splitTags = tagGroup.split(',').filter((v) => v.length > 0);
	
	splitTags.forEach((item) => {
		const [key, value] = item.split(';');
		if (typeof key !== 'string' || typeof value !== 'string') return;
		tags[key.toLowerCase()] = value.toLowerCase();
	});
	return tags;
};

/**
 * Looks for certain referrer domains and returns either a string for that domain or "other"
 * @returns {string}
 */
function getReferrerDomain() {
	if (!window?.document?.referrer) return;
	const host = new URL(window.document.referrer).host;
	const baseDomain = host.split('.').slice(-2).join('.');
	
	const keyMap = {
		'facebook.com': 'facebook',
		'google.com': 'google',
		'twitter.com': 'twitter',
		'x.com': 'twitter',
		't.co': 'twitter',
	};

	if (keyMap[baseDomain]) return keyMap[baseDomain];
	return 'other';
}

/**
 * List of keys that can be carried over from mdManager
 * @type {Array}
 */
export const validKeys = [
	{ key: 'site', transform: [normalizeSiteValue] },
	'editorialtracking',
	{ key: 'type', transform: [overridePageType] },
	{ key: 'pagetype', transform: [normalizePageType, overridePageType] },
	'keyterm',
	'adkey1',
	'talentName',
	'showName',
	'keyword',
	'contenttag1',
	'contenttag2',
	'categorydspname',
	{ key: 'categorydspname', mappedKey: 'category' },
	'photocount',
	'vgncontent',
	'show_abbr',
	'sniads_regression_test',
	'ing',
	'scripps160',
	'scripps250',
	'scripps600',
	'scripps728',
	'scripps320',
	'scripps250btf',
	'scripps250m',
	'cs_debug',
	'sponsorship',
	{ key: 'sponsorship', mappedKey: 'topic' },
	'subsection',
	'subsection1',
	'subsection2',
	'subsctn1',
	'subsctn2',
	'source',
	'livingsource',
	'orntn',
	'version',
	'sbi_apoc',
	'sbi_size',
	'sbi_dozer',
	'glist',
	'mcid',
	'aam_fw',
	'classification',
	'templatetype',
	{
		key: 'classification',
		mappedKey: 'classification_list',
		transform: [t.split(','), t.mapTransform(t.toLowerCase)],
	},
	'strnativekey',
	'abkey',
	'kuid',
	'ksg',
	'light_mode',
	'delivery_channel',
	{ key: 'delivery_channel', mappedKey: 'layout' },
	{ key: 'sctndspname', transform: [t.toLowerCase] },
	'targetedterms',
	'uniqueid',
	{
		key: 'uniqueid',
		mappedKey: 'page_uniqueid',
		transform: [t.split('|'), t.mapTransform(t.toLowerCase)],
	},
	{
		key: 'taggroup1',
		mappedKey: 'taggroup1',
		transform: [parseTagGroup],
	},
	{ key: 'editorialtracking', mappedKey: 'is_sweeps_page', transform: [isSweepsPage] },
	{ key: 'editorialtracking', mappedKey: 'is_sweeps_entry', transform: [isSweepsEntry] },
	{ key: 'editorialtracking', mappedKey: 'is_sweeps_thanks', transform: [isSweepsThanks] },
	{ key: '', mappedKey: 'privacyoptout', transform: [hasUCLPrivacyOptOut()] },
	{ key: '', mappedKey: 'refdom', transform: [getReferrerDomain] },
	{
		key: 'mainingredient',
		mappedKey: 'main_ing',
		transform: [
			t.split(';'),
			t.filter((v) => v.length > 0),
			t.mapTransform(t.sanitizeValueIncDash),
		],
	},
	{
		key: 'targetedterms',
		mappedKey: 'ing',
		transform: [
			t.split(';'),
			t.filter((v) => v.length > 0),
			t.mapTransform(t.sanitizeValueIncDash),
		],
	},

];

// Returns the result of inUserConsentState to avoid constant calls. Returns a function that returns
//	the value because mapKeyValues function expects transforms as functions
export function hasUCLPrivacyOptOut() {
	const consentStates = ['data-store', 'ads-contextual', 'ads-person-prof', 'ads-person', 'vendor'];
	let privacyOptOut = false;
	
	if (window.WM?.UserConsent?.inUserConsentState(consentStates, {id: 'MCP'}) === false) {
		privacyOptOut = true;
	}

	return () => privacyOptOut;
}

export function getCookieValues() {
	const cookies = cookie.parse(window.document.cookie);
	const validCookies = {};

	if (cookies.aam_fw) {
		validCookies.aam_fw = cookies.aam_fw.split(';').map(i => i.split('=')[1]);
	}

	validCookies.us_privacy = cookies.us_privacy;
	validCookies.usprivacy = cookies.usprivacy;

	if (cookies['AMCV_BC501253513148ED0A490D45%40AdobeOrg']) {
		const adobeCookie = ['id', ...cookies['AMCV_BC501253513148ED0A490D45%40AdobeOrg'].split('|')]
			.reduce((prev, current, index, array) =>
				(index % 2 === 0 ? { ...prev, [current]: array[index + 1] } : prev),
			{});

		validCookies.mcid = adobeCookie.MCMID;
	}

	// Add Prism identifier
	validCookies.wmuk = cookies.datid || cookies.WMUKID_STABLE;
	validCookies.ppid = cookies.datid || cookies.WMUKID_STABLE;

	return validCookies;
}

/**
 * Retrieves meta data from mdManager and returns it
 * @return {Object} Meta data from mdManager
 */
export default function standardSiteMetaData() {
	try {
		const { mdManager } = window;
		const { hostname, search } = window.location;
		/* eslint-disable-next-line camelcase */
		const { ad_topic, source } = queryString.parse(search);

		const getValueFromMdManager = key => mdManager.getParameterString(key);

		let keyValues = mapKeyValues(validKeys, getValueFromMdManager);

		const defaultTransform = [t.sanitizeValueIncDash];

		const cookieValues = getCookieValues();

		// Add querystring parameters to page meta, if they exist
		/* eslint-disable camelcase */
		if (ad_topic !== undefined) {
			keyValues.topic = ad_topic;
		}
		/* eslint-enable camelcase */

		if (source !== undefined) {
			keyValues.source = source;
		}

		// Workaround for identifying author and golden sites, so we can serve test ads to them
		if (/^author\./i.test(hostname)) keyValues.environment = 'author';
		if (/^golden-www\./i.test(hostname)) keyValues.environment = 'golden';

		keyValues.config_channel = 'standard';

		return { ...keyValues, ...cookieValues };
	} catch (e) {
		throw new Error(e);
	}
}
